import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";

const humanize = (s) => String(s).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const CAT = { collective: "Collective", individuelle: "Individuelle", technique: "Technique", organisationnelle: "Organisationnelle" };

export default function QuestionnaireReglementaire({ operationId, initialAnswers, onUpdate }) {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get(`/operations/${operationId}/reglementaire`).then((r) => setData(r.data));
  }, [operationId]);

  const push = (next) => {
    setSaving(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const { data: res } = await api.post(`/operations/${operationId}/questionnaire`, { answers: next });
      setData(res);
      setSaving(false);
      onUpdate && onUpdate();
    }, 450);
  };

  const setAns = (qid, val) => {
    const next = { ...answers, [qid]: val };
    setAnswers(next);
    push(next);
  };

  if (!data) return <div className="mono">Chargement de l'analyse réglementaire…</div>;
  if (!data.activites.length) return <div className="notice">Aucune activité à analyser. Renseignez la nature des travaux de l'opération.</div>;

  return (
    <div data-testid="questionnaire">
      <div className="notice" style={{ background: data.complet ? "var(--vert-p)" : "var(--jaune-p)", borderColor: data.complet ? "var(--vert)" : "var(--ambre-f)" }} data-testid="questionnaire-status">
        {data.complet ? "Toutes les informations obligatoires sont renseignées." : `${data.informationsManquantes.length} information(s) obligatoire(s) manquante(s) — le moteur ne conclut pas tant qu'elles ne sont pas connues.`}
        {saving && <span className="mono" style={{ marginLeft: 10, fontSize: ".62rem" }}>enregistrement…</span>}
      </div>

      {data.activites.map((act) => (
        <div className="card" key={act.code} data-testid={`activite-${act.code}`}>
          <h3 style={{ fontSize: "1.05rem", marginBottom: 4 }}>{act.nom}</h3>
          {act.obligations.map((o, i) => (
            <div key={i} style={{ fontSize: ".78rem", color: "var(--text-muted)", marginBottom: 8 }}>
              Obligation : {o.texte} {o.reference?.code && <span className="tag" style={{ marginLeft: 4 }}>{o.reference.code}</span>}
            </div>
          ))}

          <label className="f">Questions</label>
          {act.questions.map((q) => {
            const missing = act.questionsManquantes.some((m) => m.id === q.id);
            return (
              <div key={q.id} style={{ marginBottom: 10, paddingLeft: 8, borderLeft: `2px solid ${missing ? "var(--rouge)" : "var(--ligne)"}` }}>
                <div style={{ fontSize: ".84rem", marginBottom: 4 }}>{q.label}{q.requis && <span style={{ color: "var(--rouge)" }}> *</span>}</div>
                {q.aide && <div style={{ fontSize: ".72rem", color: "var(--text-muted)", marginBottom: 4 }}>{q.aide}</div>}
                {q.type === "bool" && (
                  <div className="chips" data-testid={`q-${q.id}`}>
                    <button type="button" className={`chip ${answers[q.id] === true ? "on" : ""}`} onClick={() => setAns(q.id, true)}>Oui</button>
                    <button type="button" className={`chip ${answers[q.id] === false ? "on" : ""}`} onClick={() => setAns(q.id, false)}>Non</button>
                  </div>
                )}
                {q.type === "select" && (
                  <select data-testid={`q-${q.id}`} value={answers[q.id] || ""} onChange={(e) => setAns(q.id, e.target.value)} style={{ maxWidth: 320 }}>
                    <option value="">—</option>
                    {q.options.map((o) => <option key={o} value={o}>{humanize(o)}</option>)}
                  </select>
                )}
                {q.type === "number" && (
                  <input data-testid={`q-${q.id}`} type="number" value={answers[q.id] ?? ""} onChange={(e) => setAns(q.id, e.target.value === "" ? "" : Number(e.target.value))} style={{ maxWidth: 180 }} />
                )}
                {q.type === "text" && (
                  <textarea data-testid={`q-${q.id}`} rows={2} value={answers[q.id] || ""} onChange={(e) => setAns(q.id, e.target.value)} />
                )}
              </div>
            );
          })}

          {act.mesures.length > 0 && (
            <>
              <label className="f">Mesures déterminées (justifiées)</label>
              {act.mesures.map((m) => (
                <div key={m.id} className="vitem req" style={{ borderLeft: "3px solid var(--sarcelle)", paddingLeft: 10, marginBottom: 8 }} data-testid={`mesure-${m.id}`}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span className={`badge ${m.categorie === "collective" ? "b-autorise" : m.categorie === "individuelle" ? "b-soumis" : "b-en_cours"}`}>{CAT[m.categorie] || m.categorie}</span>
                    <b style={{ fontSize: ".86rem" }}>{m.label}</b>
                  </div>
                  <div style={{ fontSize: ".8rem", color: "var(--text-muted)", marginTop: 4 }}>Pourquoi : {m.justification}</div>
                  {m.motif && <div style={{ fontSize: ".8rem", marginTop: 2 }}>Motif renseigné : « {m.motif} »</div>}
                  <div style={{ fontSize: ".72rem", marginTop: 3 }}><span className="tag">{m.reference?.code}</span> {m.reference?.libelle}</div>
                </div>
              ))}
            </>
          )}
        </div>
      ))}
    </div>
  );
}
